import { TextureSource, type WebGLRenderer } from 'pixi.js';
import type { VideoFrameSource } from '../core/definitions';
import type { Logger } from '../core/services';

/** A texture whose pixels come from a DOM media object and are uploaded on demand. */
class FrameSource extends TextureSource<VideoFrameSource> {
  constructor(resource: VideoFrameSource) {
    super({ resource, autoGarbageCollect: false, alphaMode: 'premultiply-alpha-on-upload' });
    this.uploadMethodId = 'image';
  }
}

interface Entry {
  source: FrameSource;
  version: number;
  /** Render pass in which the texture was last refreshed. */
  pass: number;
  lastUsedMs: number;
  failed: boolean;
  /** Video only: a new frame has been presented since the last upload. */
  fresh: boolean;
  lastFreshMs: number;
  frameCallback: number;
  /** Video only: currentTime at the last upload. */
  uploadedTime: number;
}

/**
 * requestVideoFrameCallback is only an optimisation: it runs with the page's
 * rendering, so it goes quiet whenever the video is not being presented
 * (hidden tab, occluded or minimised window) although frames keep arriving.
 * Once it has been silent for this long it is not trusted and a playing video
 * is uploaded on every composed frame.
 */
const FRAME_CALLBACK_TRUST_MS = 100;

function resourceWidth(resource: VideoFrameSource): number {
  if (resource instanceof HTMLVideoElement) return resource.videoWidth;
  if (resource instanceof HTMLImageElement) return resource.naturalWidth;
  if (resource instanceof VideoFrame) return resource.displayWidth;
  return resource.width;
}

function resourceHeight(resource: VideoFrameSource): number {
  if (resource instanceof HTMLVideoElement) return resource.videoHeight;
  if (resource instanceof HTMLImageElement) return resource.naturalHeight;
  if (resource instanceof VideoFrame) return resource.displayHeight;
  return resource.height;
}

/**
 * One GPU texture per media object. `acquire` refreshes the texture at most
 * once per render pass, so a source used by several items or filters is
 * uploaded once.
 */
export class TextureCache {
  private readonly entries = new Map<unknown, Entry>();

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly logger: Logger,
  ) {}

  /** Probe an upload on this compositor's GPU without caching, drawing or retaining the public source. */
  probe(resource: VideoFrameSource): boolean {
    let source: FrameSource | null = null;
    try {
      if (resourceWidth(resource) !== 1 || resourceHeight(resource) !== 1) return false;
      source = new FrameSource(resource);
      this.renderer.texture.initSource(source);
      return true;
    }
    catch { return false; }
    finally { source?.destroy(); }
  }

  /** Returns null while the object has no pixels yet (video not ready, zero-sized canvas) or cannot be uploaded. */
  acquire(
    resource: VideoFrameSource,
    version: number,
    dynamic: boolean,
    pass: number,
    nowMs: number,
    /**
     * Identity of the texture when the media object itself is replaced from
     * frame to frame (VideoFrame providers); defaults to the object.
     */
    key: unknown = resource,
  ): TextureSource | null {
    let entry = this.entries.get(key);
    if (entry && entry.pass === pass) return entry.failed ? null : entry.source;
    if (resource instanceof VideoFrame) return this.acquireFrame(resource, version, key, entry, pass, nowMs);
    const isVideo = resource instanceof HTMLVideoElement;
    if (isVideo && resource.readyState < 2) {
      // Keep showing the last uploaded frame across seeks and stalls.
      if (entry) entry.lastUsedMs = nowMs;
      return entry && !entry.failed ? entry.source : null;
    }
    if (resourceWidth(resource) <= 0 || resourceHeight(resource) <= 0) return null;

    if (!entry) {
      entry = {
        source: new FrameSource(resource),
        version,
        pass,
        lastUsedMs: nowMs,
        failed: false,
        fresh: false,
        lastFreshMs: nowMs,
        frameCallback: 0,
        uploadedTime: isVideo ? resource.currentTime : 0,
      };
      this.entries.set(key, entry);
      if (isVideo) this.watchVideo(resource, entry);
      this.upload(entry, true);
      return entry.failed ? null : entry.source;
    }

    entry.pass = pass;
    entry.lastUsedMs = nowMs;
    if (entry.failed) return null;
    let stale = dynamic || version !== entry.version;
    if (stale && isVideo) {
      if (resource.paused || resource.ended) {
        // A stopped element only shows something new after a seek.
        stale = entry.fresh || resource.currentTime !== entry.uploadedTime;
      } else if (entry.frameCallback !== 0) {
        stale = entry.fresh || nowMs - entry.lastFreshMs > FRAME_CALLBACK_TRUST_MS;
      }
    }
    if (stale) {
      entry.version = version;
      entry.fresh = false;
      if (isVideo) entry.uploadedTime = resource.currentTime;
      this.upload(entry, false);
    }
    return entry.failed ? null : entry.source;
  }

  /**
   * A provider that hands over a new VideoFrame object for every picture: the
   * same texture is refilled from whichever frame is current. The frame is
   * only read during the upload; the source owns and closes it.
   */
  private acquireFrame(
    frame: VideoFrame,
    version: number,
    key: unknown,
    entry: Entry | undefined,
    pass: number,
    nowMs: number,
  ): TextureSource | null {
    // A closed frame has no pixels; keep showing what was uploaded last.
    const readable = frame.displayWidth > 0 && frame.displayHeight > 0;
    if (!entry) {
      if (!readable) return null;
      const source = new FrameSource(frame);
      try {
        this.renderer.texture.initSource(source);
      } catch {
        // The frame was closed under us; the next one gets a fresh texture.
        source.destroy();
        return null;
      }
      entry = { source, version, pass, lastUsedMs: nowMs, failed: false, fresh: false, lastFreshMs: nowMs, frameCallback: 0, uploadedTime: 0 };
      this.entries.set(key, entry);
      return entry.source;
    }
    entry.pass = pass;
    entry.lastUsedMs = nowMs;
    if (readable && (entry.source.resource !== frame || entry.version !== version)) {
      const previous = entry.source.resource;
      entry.source.resource = frame;
      try {
        // Decoded frames are converted to sRGB RGBA by the browser during the upload.
        entry.source.update();
        entry.version = version;
      } catch {
        // Closed between the check and the upload: skip it and keep the previous picture.
        entry.source.resource = previous;
      }
    }
    return entry.source;
  }

  /** Destroys textures that no render pass has asked for since `beforeMs`. */
  sweep(beforeMs: number): void {
    for (const [key, entry] of this.entries) {
      if (entry.lastUsedMs < beforeMs) {
        this.release(entry);
        this.entries.delete(key);
      }
    }
  }

  destroy(): void {
    for (const entry of this.entries.values()) this.release(entry);
    this.entries.clear();
  }

  private upload(entry: Entry, first: boolean): void {
    try {
      // The first bind uploads; afterwards update() re-uploads (and reallocates on a size change).
      if (first) this.renderer.texture.initSource(entry.source);
      else entry.source.update();
    } catch (error) {
      entry.failed = true;
      this.logger.log('error', 'render', 'Could not upload a video frame to the GPU; the source is not drawn', error);
    }
  }

  private watchVideo(video: HTMLVideoElement, entry: Entry): void {
    if (typeof video.requestVideoFrameCallback !== 'function') return;
    const onFrame = (): void => {
      entry.fresh = true;
      entry.lastFreshMs = performance.now();
      entry.frameCallback = video.requestVideoFrameCallback(onFrame);
    };
    entry.frameCallback = video.requestVideoFrameCallback(onFrame);
  }

  private release(entry: Entry): void {
    const resource = entry.source.resource;
    if (entry.frameCallback !== 0 && resource instanceof HTMLVideoElement) {
      resource.cancelVideoFrameCallback(entry.frameCallback);
      entry.frameCallback = 0;
    }
    entry.source.destroy();
  }
}
